import React from 'react';
import styled from 'styled-components';
import { FaPaw, FaStethoscope, FaSyringe, FaCut, FaMicroscope, FaUserMd } from 'react-icons/fa';
import { IconType } from 'react-icons';
import Icon from './Icon';
import SectionTitle from './SectionTitle';

const ServicesSection = styled.section`
  padding: 120px 0;
  background-color: #fff;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  flex: 2;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`;

const ServiceCard = styled.div`
  background: #fff;
  padding: 2.5rem 2rem;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.07);
  text-align: center;
  transition: transform 0.25s, box-shadow 0.25s;
  border: 1px solid #f5f5f5;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 260px;
  &:hover {
    transform: translateY(-8px) scale(1.03);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.13);
    border-color: #ff9d5c44;
    .service-icon {
      background-color: #ff9d5c;
      color: #fff;
      transform: scale(1.12);
    }
  }
  h3 {
    font-size: 1.35rem;
    margin: 1.2rem 0 0.7rem;
    color: #2c3e50;
    font-weight: 700;
  }
  p {
    color: #666;
    line-height: 1.6;
    font-size: 1.05rem;
    margin-bottom: 0.5rem;
  }
`;

const ServiceIcon = styled.div`
  width: 90px;
  height: 90px;
  margin: 0 auto;
  background-color: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: #ff9d5c;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(255,157,92,0.08);
`;

const PawDecoration = styled.div`
  position: absolute;
  z-index: 1;
  opacity: 0.05;

  @media (max-width: 768px) {
    display: none;
  }

  &.paw1 {
    top: 10%;
    right: 5%;
    transform: rotate(45deg);
  }

  &.paw2 {
    bottom: 15%;
    left: 5%;
    transform: rotate(-30deg);
  }

  &.paw3 {
    top: 50%;
    right: 15%;
    transform: rotate(15deg);
  }

  &.paw4 {
    top: 25%;
    left: 20%;
    transform: rotate(-15deg);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const DogImageTitle = styled.img`
  max-width: 200px;
  margin: 0 auto;
  display: block;
`;

const SectionDescription = styled.p`
  color: #666;
  font-size: 1.1rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.6;
`;

interface Service {
  icon: IconType;
  title: string;
  description: string;
}

interface ServicesProps {
  id?: string;
}

const Services: React.FC<ServicesProps> = ({ id }) => {
  const services: Service[] = [
    {
      icon: FaStethoscope,
      title: 'Consultas',
      description: 'Atendimento veterinário especializado para seu pet com profissionais qualificados.'
    },
    {
      icon: FaSyringe,
      title: 'Vacinas',
      description: 'Programa completo de vacinação com as melhores vacinas do mercado.'
    },
    {
      icon: FaMicroscope,
      title: 'Exames',
      description: 'Exames laboratoriais e de imagem para diagnósticos precisos e eficazes.'
    },
    {
      icon: FaCut,
      title: 'Atestados',
      description: 'Emissão de atestados de saúde e documentação para viagens nacionais e internacionais do seu pet.'
    },
    {
      icon: FaPaw,
      title: 'Pet Shop',
      description: 'Banho, tosa e produtos especiais para a higiene e bem-estar do seu pet.'
    },
    {
      icon: FaUserMd,
      title: 'Cirurgias Gerais',
      description: 'Procedimentos cirúrgicos realizados com equipamentos modernos e equipe especializada.'
    }
  ];

  return (
    <ServicesSection id={id}>
      <Container style={{ position: 'relative' }}>
        <SectionHeader>
          <SectionTitle>Nossos Serviços</SectionTitle>
          <DogImageTitle src="/images/servicos.png" alt="Cachorro feliz" />
        </SectionHeader>
        <SectionDescription>
          Oferecemos diversos serviços veterinários com excelência, garantindo a saúde e o bem-estar do seu pet.
        </SectionDescription>
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard key={index}>
              <ServiceIcon className="service-icon">
                <Icon icon={service.icon} size={40} color="currentColor" />
              </ServiceIcon>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
      
      {/* Patinhas decorativas */}
      <PawDecoration className="paw1">
        <Icon icon={FaPaw} size={200} color="#000" />
      </PawDecoration>
      <PawDecoration className="paw2">
        <Icon icon={FaPaw} size={250} color="#000" />
      </PawDecoration>
      <PawDecoration className="paw3">
        <Icon icon={FaPaw} size={180} color="#000" />
      </PawDecoration>
      <PawDecoration className="paw4">
        <Icon icon={FaPaw} size={220} color="#000" />
      </PawDecoration>
    </ServicesSection>
  );
};

export default Services; 